import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PhoneCall, CalendarClock } from "lucide-react";
import { fmtDateTime, fmtDate } from "@/lib/format";

const isoIn = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString("en-CA"); };

const CallbackPicker = ({ value, onChange }) => (
  <div className="space-y-1">
    <p className="text-[11px] font-semibold text-slate-500">Call back on (optional)</p>
    <div className="flex flex-wrap items-center gap-1.5">
      {[[1, "Tomorrow"], [2, "In 2 days"], [7, "Next week"]].map(([n, l]) => (
        <button key={n} type="button" onClick={() => onChange(value === isoIn(n) ? "" : isoIn(n))} data-testid={`refill-callback-quick-${n}`}
          className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${value === isoIn(n) ? "border-cyan-600 bg-cyan-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>))}
      <Input type="date" min={isoIn(0)} value={value} onChange={(e) => onChange(e.target.value)} data-testid="refill-callback-date" className="h-7 w-36 text-xs" />
    </div>
  </div>
);

export const OUTCOMES = {
  reached: "Called, reached", voicemail: "Left voicemail", no_answer: "No answer",
  will_refill: "Will refill", declined: "Doesn't want refill", other: "Other",
};

export const lastLog = (r) => (r.refill_log?.length ? r.refill_log[r.refill_log.length - 1] : null);

export const RefillLogSummary = ({ r }) => {
  const l = lastLog(r);
  if (!l) return <span className="text-xs text-slate-400">Not contacted</span>;
  return (
    <span data-testid={`refill-last-${r.id}`} className="text-xs text-slate-600" title={r.refill_log.map((x) => `${fmtDateTime(x.at)} · ${OUTCOMES[x.outcome]}${x.note ? ` — ${x.note}` : ""} (${x.by})`).join("\n")}>
      <span className="font-semibold">{OUTCOMES[l.outcome]}</span>{l.note ? ` — ${l.note}` : ""}
      <span className="block text-[11px] text-slate-400">{l.by} · {fmtDateTime(l.at)}{r.refill_log.length > 1 ? ` · ${r.refill_log.length} attempts` : ""}</span>
      {r.callback_date && (
        <span data-testid={`refill-callback-${r.id}`} className={`mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${r.callback_due ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"}`}>
          <CalendarClock className="h-3 w-3" />{r.callback_due ? "Call back today" : `Call back ${fmtDate(`${r.callback_date}T12:00:00`)}`}
        </span>)}
    </span>
  );
};

export const RefillNoteButton = ({ r }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [cb, setCb] = useState("");
  const save = useMutation({ mutationFn: (outcome) => api.post(`/medications/${r.id}/refill-note`, { outcome, note, callback_date: cb || null }),
    onSuccess: (_, o) => { toast.success(`Logged "${OUTCOMES[o]}" for ${r.patient?.name}${cb ? ` — call back ${fmtDate(`${cb}T12:00:00`)}` : ""}`); setNote(""); setCb(""); setOpen(false); qc.invalidateQueries({ queryKey: ["medications"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save") });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="mr-1 h-7" title="Log call" aria-label="Log call" data-testid={`refill-log-${r.id}`}>
          <PhoneCall className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Log call</span></Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-2 p-3" data-testid={`refill-log-popover-${r.id}`}>
        <p className="text-xs font-semibold text-slate-700">Log follow-up for {r.patient?.name}</p>
        <Input data-testid="refill-log-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Optional note, e.g. call back Friday" className="h-8 text-xs" />
        <CallbackPicker value={cb} onChange={setCb} />
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(OUTCOMES).map(([k, l]) => (
            <Button key={k} size="sm" variant="outline" className="h-7 text-xs" disabled={save.isPending || (k === "other" && !note.trim())}
              onClick={() => save.mutate(k)} data-testid={`refill-outcome-${k}`}>{l}</Button>))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
